import React, { Component } from "react";
import { connect } from "react-redux";
import { BlueBtn, TextBtn } from "components/Controls";
import { logEvent } from "containers/EmbeddableWidget/actions";
import { tokenSelector } from "containers/EmbeddableWidget/selectors";
import { ProvidersApi } from "api/providers";
import SnapLogo from "assets/img/snap-logo.png";
import {
  activeStepDataSelector,
  initApplicationResponseSelector,
} from "../../selectors";
import {
  backToOffers,
  nextStep,
  selectOffer,
  selectOfferSuccess,
} from "../../actions";

import "./styles.scss";

const DEFAULT_PENDING_MESSAGE =
  "We're going to ask you a few more questions before going forward. " +
  "You'll get a Snap Finance SMS message on your phone with a link you can use to answer the questions. " +
  "When you're done, come on back to this page to resume your application.";

// Minimum delay between two "Check for updates" requests (seconds).
const CHECK_COOLDOWN_SECONDS = 7;

const PhoneSmsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="6" y="2.5" width="12" height="19" rx="2.5" stroke="#f58220" strokeWidth="1.6" />
    <path d="M10 5.5h4" stroke="#f58220" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="12" cy="18.3" r="0.9" fill="#f58220" />
    <path
      d="M8.5 9.5h7a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-3.2l-2.3 1.8v-1.8h-1.5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1z"
      fill="#f58220"
    />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="8" r="1" fill="currentColor" />
  </svg>
);

class SnapPendingScreen extends Component {
  state = {
    loading: false,
    stillPending: false,
    error: false,
    smsError: !!(this.props.data && this.props.data.smsError),
    freshMessage: null,
    cooldown: 0,
  };

  componentWillUnmount() {
    this.clearCooldown();
  }

  handleBackButton = () => {
    const { backToOffers, logEvent } = this.props;
    backToOffers();
    logEvent({ name: "view other options" });
  };

  clearCooldown = () => {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
  };

  startCooldown = () => {
    this.clearCooldown();
    this.setState({ cooldown: CHECK_COOLDOWN_SECONDS });
    this.cooldownTimer = setInterval(() => {
      this.setState((prev) => {
        if (prev.cooldown <= 1) {
          this.clearCooldown();
          return { cooldown: 0 };
        }
        return { cooldown: prev.cooldown - 1 };
      });
    }, 1000);
  };

  getPendingMessage = () => {
    const { data } = this.props;
    return (
      this.state.freshMessage ||
      data.pending_message ||
      (data.data && data.data.pending_message) ||
      DEFAULT_PENDING_MESSAGE
    );
  };

  getRequestId = () => {
    const { data, applicationResponse } = this.props;
    return (
      (applicationResponse && applicationResponse.request_id) ||
      (data.app && data.app.request_id)
    );
  };

  getLeaseAppId = () => {
    const { data } = this.props;
    return (
      data.lease_app_id ||
      (data.app && data.app.lease_app_id) ||
      (data.data && data.data.application_id)
    );
  };

  // Snap approved. Two ways to get here:
  //  - "select": the customer picked a pending Snap offer on the offers step.
  //    Re-select the refreshed offer so the regular snap flow continues
  //    (lease explainer -> bank data -> accept).
  //  - "accept": accept_the_offer itself came back pending (bank step-up).
  //    Resume from selectOfferSuccess -> snapConfirmation.
  handleApproved = (result) => {
    const { data } = this.props;

    if (data.pendingSource === "select") {
      const { pendingSource, smsError, ...offer } = data;
      this.props.selectOffer({
        selectedOffer: {
          ...offer,
          ...(result.offer || {}),
          pending: false,
          pending_verification: false,
        },
      });
      return;
    }

    this.props.selectOfferSuccess({
      selectedOffer: { app: result.app, data: result.data },
      isContractOpened: false,
    });
  };

  handleDenied = () => {
    this.props.nextStep({ step: "nobodyApprovedStep" });
  };

  handleCheckStatus = () => {
    const { token } = this.props;
    const { loading, cooldown } = this.state;

    if (loading || cooldown > 0) return;

    this.setState({ loading: true, error: false, smsError: false });
    this.startCooldown();

    ProvidersApi.snapCheckStatus(token, {
      request_id: this.getRequestId(),
      lease_app_id: this.getLeaseAppId(),
    })
      .then((result) => {
        if (!result || result.errors) {
          this.setState({ loading: false, error: true });
          return;
        }
        if (result.pending) {
          this.setState({
            loading: false,
            stillPending: true,
            freshMessage: result.pending_message,
          });
          return;
        }
        this.setState({ loading: false, stillPending: false });
        if (result.application_status === "DENIED") {
          this.handleDenied();
          return;
        }
        this.handleApproved(result);
      })
      .catch(() => {
        this.setState({ loading: false, error: true });
      });
  };

  render() {
    const { loading, stillPending, error, smsError, cooldown } = this.state;
    const waiting = cooldown > 0 && !loading;
    const buttonLabel = waiting
      ? `Check for updates (${cooldown})`
      : "Check for updates";

    return (
      <div className="step5__content flex--column paypair-step-content snap-pending">
        <TextBtn
          customClass="snap-pending__back-btn"
          onClick={this.handleBackButton}
        >
          View Other Options
        </TextBtn>
        <div className="snap-pending__header">
          <h1 className="snap-pending__title">Pay with Snap</h1>
          <img className="snap-pending__logo" src={SnapLogo} alt="Snap Finance" />
        </div>

        <div className="snap-pending__card">
          <div className="snap-pending__hero">
            <div className="snap-pending__icon">
              <PhoneSmsIcon />
            </div>
            <div>
              <p className="snap-pending__lead">Almost there! Check your phone.</p>
              <p className="snap-pending__sub">
                Snap needs to verify a few more details before we can continue.
              </p>
            </div>
          </div>

          <p className="snap-pending__message">{this.getPendingMessage()}</p>

          <ol className="snap-pending__steps">
            <li className="snap-pending__step">
              <span className="snap-pending__step-num">1</span>
              <span>Open the SMS from Snap Finance on your phone.</span>
            </li>
            <li className="snap-pending__step">
              <span className="snap-pending__step-num">2</span>
              <span>Follow the link and answer the questions.</span>
            </li>
            <li className="snap-pending__step">
              <span className="snap-pending__step-num">3</span>
              <span>Come back here and press “Check for updates”.</span>
            </li>
          </ol>

          {stillPending && !error && (
            <div className="snap-pending__note" role="status">
              <InfoIcon />
              <span>
                Your application is still pending. Finish the steps from the
                Snap SMS link, then check again.
              </span>
            </div>
          )}
          {error && (
            <div className="snap-pending__note snap-pending__note--error" role="alert">
              <InfoIcon />
              <span>We couldn't check your application status. Please try again.</span>
            </div>
          )}
          {smsError && !error && (
            <div className="snap-pending__note snap-pending__note--error" role="alert">
              <InfoIcon />
              <span>
                We couldn't send the Snap SMS right now. If you don't receive
                a text shortly, please contact Snap Finance support.
              </span>
            </div>
          )}

          <div className="snap-pending__actions">
            <BlueBtn
              customClass="snap-pending__btn"
              loading={loading}
              disabled={waiting}
              onClick={this.handleCheckStatus}
            >
              {buttonLabel}
            </BlueBtn>
            <p className="snap-pending__hint">
              {waiting
                ? "Give Snap a few seconds to update your application."
                : "It may take a moment for Snap to update your status."}
            </p>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  data: activeStepDataSelector()(state),
  applicationResponse: initApplicationResponseSelector()(state),
  token: tokenSelector()(state),
});

const mapDispatchToProps = (dispatch) => {
  return {
    backToOffers: () => dispatch(backToOffers()),
    logEvent: (value) => dispatch(logEvent(value)),
    nextStep: (value) => dispatch(nextStep(value)),
    selectOffer: (value) => dispatch(selectOffer(value)),
    selectOfferSuccess: (value) => dispatch(selectOfferSuccess(value)),
  };
};

SnapPendingScreen = connect(
  mapStateToProps,
  mapDispatchToProps
)(SnapPendingScreen);

export default SnapPendingScreen;
